import React, { useState, useEffect } from "react";
import { api } from "@/context/AuthContext";
import {
  KeyRound, Plus, Trash2, Copy, RefreshCw, CheckCircle, AlertCircle,
  Webhook, Send, ChevronDown, ChevronRight, BookOpen, Eye, EyeOff,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const EVENTS = [
  { value: "survey.completed",     desc: "Insured finishes a survey — includes risk score & findings count" },
  { value: "report.finalized",     desc: "Underwriter finalizes a report — pull the PDF via the API" },
  { value: "eligibility.assessed", desc: "AI eligibility verdict ready (eligible / ineligible / needs_review)" },
];

function CopyBtn({ text, testid }) {
  const [copied, setCopied] = useState(false);
  return (
    <button type="button" data-testid={testid}
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
      className="flex items-center gap-1 text-xs text-navy hover:underline flex-shrink-0">
      {copied ? <CheckCircle className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function ApiKeysSection() {
  const [keys, setKeys] = useState([]);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [freshKey, setFreshKey] = useState(null);

  const load = () => api.get("/carrier/api-keys").then(r => setKeys(r.data)).catch(console.error);
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setCreating(true);
    try {
      const res = await api.post("/carrier/api-keys", { name: newName || "Default" });
      setFreshKey(res.data);
      setNewName("");
      load();
    } catch (e) { console.error(e); }
    setCreating(false);
  };

  const revoke = async (id) => {
    await api.delete(`/carrier/api-keys/${id}`);
    load();
  };

  return (
    <div className="space-y-4" data-testid="api-keys-section">
      <div>
        <h3 className="font-heading font-semibold text-slate-800">API Keys</h3>
        <p className="text-sm text-slate-500 mt-0.5">Authenticate API requests with the <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">X-API-Key</code> header. Keys are shown once and stored hashed.</p>
      </div>

      <div className="flex items-center gap-2">
        <input data-testid="api-key-name-input" value={newName} onChange={e => setNewName(e.target.value)}
          placeholder="Key name (e.g., Guidewire integration)"
          className="h-10 px-3 border border-slate-200 rounded-lg text-sm w-72 focus:outline-none focus:ring-2 focus:ring-navy/30" />
        <button data-testid="generate-api-key-btn" onClick={generate} disabled={creating}
          className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition-colors disabled:opacity-50">
          <Plus className="w-4 h-4" /> {creating ? "Generating…" : "Generate Key"}
        </button>
      </div>

      {freshKey && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="fresh-api-key">
          <p className="text-sm font-semibold text-amber-800 mb-2">Copy your new key now — it will not be shown again.</p>
          <div className="flex items-center gap-3 bg-white border border-amber-200 rounded-lg px-3 py-2">
            <code className="text-xs text-slate-700 break-all flex-1" data-testid="fresh-api-key-value">{freshKey.api_key}</code>
            <CopyBtn text={freshKey.api_key} testid="copy-fresh-key-btn" />
          </div>
        </div>
      )}

      {keys.length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-400 uppercase tracking-wider border-b border-slate-100">
              <th className="py-2">Name</th><th>Prefix</th><th>Created</th><th>Last Used</th><th>Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {keys.map(k => (
              <tr key={k.id} className="border-b border-slate-50" data-testid={`api-key-row-${k.id}`}>
                <td className="py-2.5 font-medium text-slate-700">{k.name}</td>
                <td><code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">rsk_live_{k.prefix}_…</code></td>
                <td className="text-slate-500 text-xs">{k.created_at?.split("T")[0]}</td>
                <td className="text-slate-500 text-xs">{k.last_used_at ? k.last_used_at.split("T")[0] : "Never"}</td>
                <td>
                  <span className={`text-xs font-semibold ${k.revoked ? "text-red-500" : "text-green-600"}`}>
                    {k.revoked ? "Revoked" : "Active"}
                  </span>
                </td>
                <td className="text-right">
                  {!k.revoked && (
                    <button data-testid={`revoke-key-${k.id}`} onClick={() => revoke(k.id)}
                      className="text-slate-300 hover:text-red-500 p-1"><Trash2 className="w-4 h-4" /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function WebhooksSection() {
  const [ws, setWs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  const [deliveries, setDeliveries] = useState([]);
  const [error, setError] = useState("");

  const loadDeliveries = () => api.get("/carrier/webhook-deliveries").then(r => setDeliveries(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/carrier/webhook-settings").then(r => setWs(r.data)).catch(console.error);
    loadDeliveries();
  }, []);

  if (!ws) return <div className="py-6 flex justify-center"><RefreshCw className="w-5 h-5 animate-spin text-navy" /></div>;

  const toggleEvent = (v) => setWs(prev => ({
    ...prev,
    events: prev.events.includes(v) ? prev.events.filter(e => e !== v) : [...prev.events, v],
  }));

  const save = async () => {
    setSaving(true); setError("");
    try {
      const res = await api.put("/carrier/webhook-settings", ws);
      setWs(res.data);
      setSaved(true); setTimeout(() => setSaved(false), 2500);
    } catch (e) { setError(e.response?.data?.detail || "Failed to save"); }
    setSaving(false);
  };

  const rotate = async () => {
    const res = await api.post("/carrier/webhook-settings/rotate-secret");
    setWs(prev => ({ ...prev, secret: res.data.secret }));
    setShowSecret(true);
  };

  const sendTest = async () => {
    setTesting(true); setTestResult(null); setError("");
    try {
      const res = await api.post("/carrier/webhook-test");
      setTestResult(res.data);
      loadDeliveries();
    } catch (e) { setError(e.response?.data?.detail || "Test failed"); }
    setTesting(false);
  };

  return (
    <div className="space-y-4 pt-6 border-t border-slate-100" data-testid="webhooks-section">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-heading font-semibold text-slate-800 flex items-center gap-2"><Webhook className="w-4 h-4 text-navy" /> Webhooks</h3>
          <p className="text-sm text-slate-500 mt-0.5">We POST signed events to your endpoint so your core systems stay in sync automatically.</p>
        </div>
        <label className="flex items-center gap-2 text-sm font-medium text-slate-600 cursor-pointer">
          <input type="checkbox" data-testid="webhook-enabled-toggle" checked={ws.enabled} onChange={e => setWs({ ...ws, enabled: e.target.checked })} className="accent-navy w-4 h-4" />
          Enabled
        </label>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-600 mb-1.5">Endpoint URL</label>
        <input data-testid="webhook-url-input" value={ws.url} onChange={e => setWs({ ...ws, url: e.target.value })}
          placeholder="https://your-system.example.com/webhooks/risksurvey"
          className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30" />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-600 mb-2">Events to send</label>
        <div className="space-y-1.5">
          {EVENTS.map(ev => (
            <label key={ev.value} className="flex items-start gap-2.5 cursor-pointer" data-testid={`webhook-event-${ev.value}`}>
              <input type="checkbox" checked={ws.events.includes(ev.value)} onChange={() => toggleEvent(ev.value)} className="mt-0.5 accent-navy" />
              <span className="text-sm text-slate-700"><code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{ev.value}</code>
                <span className="text-slate-400 text-xs ml-2">{ev.desc}</span></span>
            </label>
          ))}
        </div>
      </div>

      {ws.secret && (
        <div>
          <label className="block text-sm font-medium text-slate-600 mb-1.5">Signing Secret <span className="text-slate-400 font-normal">(verify the X-RiskSurvey-Signature header)</span></label>
          <div className="flex items-center gap-2">
            <code className="text-xs bg-slate-100 border border-slate-200 rounded-lg px-3 py-2 flex-1" data-testid="webhook-secret">
              {showSecret ? ws.secret : "whsec_••••••••••••••••••••"}
            </code>
            <button type="button" onClick={() => setShowSecret(s => !s)} className="p-2 text-slate-400 hover:text-navy" data-testid="toggle-secret-btn">
              {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
            {showSecret && <CopyBtn text={ws.secret} testid="copy-secret-btn" />}
            <button type="button" onClick={rotate} className="text-xs text-slate-500 hover:text-navy underline" data-testid="rotate-secret-btn">Rotate</button>
          </div>
        </div>
      )}

      {error && <p className="text-sm text-red-600 flex items-center gap-1.5" data-testid="webhook-error"><AlertCircle className="w-4 h-4" /> {error}</p>}

      <div className="flex items-center gap-3">
        <button data-testid="save-webhook-btn" onClick={save} disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition-colors disabled:opacity-50">
          {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />} Save Webhook Settings
        </button>
        <button data-testid="test-webhook-btn" onClick={sendTest} disabled={testing || !ws.enabled || !ws.url}
          className="flex items-center gap-2 px-4 py-2 border border-navy/30 text-navy rounded-lg text-sm font-medium hover:bg-navy/5 transition-colors disabled:opacity-50">
          <Send className="w-4 h-4" /> {testing ? "Sending…" : "Send Test Event"}
        </button>
        {saved && <span className="text-green-600 text-sm font-medium flex items-center gap-1"><CheckCircle className="w-4 h-4" /> Saved</span>}
        {testResult && (
          <span className={`text-sm font-medium ${testResult.success ? "text-green-600" : "text-red-600"}`} data-testid="test-result">
            {testResult.success ? `Delivered (HTTP ${testResult.status_code})` : `Failed: ${testResult.error}`}
          </span>
        )}
      </div>

      {deliveries.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 mt-2">Recent Deliveries</p>
          <table className="w-full text-sm" data-testid="webhook-deliveries-table">
            <thead>
              <tr className="text-left text-xs text-slate-400 uppercase tracking-wider border-b border-slate-100">
                <th className="py-2">Event</th><th>Status</th><th>When</th>
              </tr>
            </thead>
            <tbody>
              {deliveries.slice(0, 10).map(d => (
                <tr key={d.id} className="border-b border-slate-50">
                  <td className="py-2"><code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{d.event}</code></td>
                  <td>
                    <span className={`text-xs font-semibold ${d.success ? "text-green-600" : "text-red-500"}`}>
                      {d.success ? `✓ ${d.status_code}` : `✗ ${d.error || d.status_code || "failed"}`}
                    </span>
                  </td>
                  <td className="text-xs text-slate-400">{d.created_at?.replace("T", " ").slice(0, 16)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CodeBlock({ children, testid }) {
  return (
    <div className="relative group">
      <pre className="bg-slate-800 text-slate-100 text-xs rounded-lg p-4 overflow-x-auto whitespace-pre" data-testid={testid}>{children}</pre>
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={() => navigator.clipboard.writeText(children)} className="text-xs px-2 py-1 bg-white/10 text-white rounded hover:bg-white/20">Copy</button>
      </div>
    </div>
  );
}

function DocSection({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50 transition-colors"
        data-testid={`doc-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
        <span className="font-medium text-slate-700 text-sm">{title}</span>
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </button>
      {open && <div className="px-4 pb-4 space-y-3">{children}</div>}
    </div>
  );
}

function ApiDocsSection() {
  return (
    <div className="space-y-3 pt-6 border-t border-slate-100" data-testid="api-docs-section">
      <div>
        <h3 className="font-heading font-semibold text-slate-800 flex items-center gap-2"><BookOpen className="w-4 h-4 text-navy" /> API Documentation</h3>
        <p className="text-sm text-slate-500 mt-0.5">Base URL: <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{API_BASE}/api/v1</code> — all requests require the <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">X-API-Key</code> header.</p>
      </div>

      <DocSection title="Authentication" defaultOpen>
        <p className="text-sm text-slate-600">Pass your API key in every request. Keys are carrier-scoped — you only see your own data.</p>
        <CodeBlock testid="doc-auth-example">{`curl ${API_BASE}/api/v1/orders \\
  -H "X-API-Key: rsk_live_xxxxxxxx_yyyyyyyy..."`}</CodeBlock>
      </DocSection>

      <DocSection title="POST /orders — Create a survey order">
        <p className="text-sm text-slate-600">Creates an order with one inspection per coverage line. The insured automatically receives one email with a survey hub link. Returns <code className="text-xs bg-slate-100 px-1 rounded">201</code> with the order object.</p>
        <CodeBlock testid="doc-create-order">{`curl -X POST ${API_BASE}/api/v1/orders \\
  -H "X-API-Key: rsk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "insured_name": "Acme Manufacturing LLC",
    "insured_email": "contact@acme.example.com",
    "property_address": "1234 Main Street",
    "city": "Dallas",
    "state": "TX",
    "policy_number": "CP-2026-00415",
    "inspection_types": ["commercial_property", "general_liability"],
    "mode": "ai_chat",
    "business_profile_id": "optional-profile-uuid",
    "due_date": "2026-09-30"
  }'`}</CodeBlock>
        <p className="text-xs text-slate-500">
          <strong>inspection_types</strong>: commercial_property, general_liability, workers_comp, habitational, commercial_auto (1–5 per order).
          <strong className="ml-2">mode</strong>: <code>ai_chat</code> (conversational AI) or <code>form</code> (structured questionnaire).
          <strong className="ml-2">business_profile_id</strong>: optional — triggers AI eligibility assessment.
        </p>
      </DocSection>

      <DocSection title="GET /orders — List & track orders">
        <CodeBlock testid="doc-list-orders">{`# All orders (newest first, paginated)
curl "${API_BASE}/api/v1/orders?limit=25&offset=0" -H "X-API-Key: rsk_live_..."

# Filter by status: pending | in_progress | completed | finalized
curl "${API_BASE}/api/v1/orders?status=completed" -H "X-API-Key: rsk_live_..."

# Single order
curl ${API_BASE}/api/v1/orders/{order_id} -H "X-API-Key: rsk_live_..."`}</CodeBlock>
        <p className="text-sm text-slate-600">Each order includes per-line status, risk score/level, eligibility verdict, and the insured survey URL.</p>
      </DocSection>

      <DocSection title="GET /orders/{id}/report — Pull findings or PDF">
        <CodeBlock testid="doc-get-report">{`# Structured findings JSON (risk scores, executive summary, eligibility, findings)
curl ${API_BASE}/api/v1/orders/{order_id}/report -H "X-API-Key: rsk_live_..."

# Finalized PDF (consolidated for multi-line orders)
curl "${API_BASE}/api/v1/orders/{order_id}/report?format=pdf" \\
  -H "X-API-Key: rsk_live_..." -o report.pdf`}</CodeBlock>
        <p className="text-xs text-slate-500">Returns <code>409</code> until at least one survey on the order is completed.</p>
      </DocSection>

      <DocSection title="Webhooks — Signature verification">
        <p className="text-sm text-slate-600">Each delivery includes headers <code className="text-xs bg-slate-100 px-1 rounded">X-RiskSurvey-Event</code>, <code className="text-xs bg-slate-100 px-1 rounded">X-RiskSurvey-Id</code>, <code className="text-xs bg-slate-100 px-1 rounded">X-RiskSurvey-Timestamp</code> and <code className="text-xs bg-slate-100 px-1 rounded">X-RiskSurvey-Signature</code>. The signature is HMAC-SHA256 of <code className="text-xs bg-slate-100 px-1 rounded">timestamp + "\n" + raw_body</code> using your signing secret. We retry failed deliveries 3× with backoff.</p>
        <CodeBlock testid="doc-webhook-verify">{`# Python receiver example
import hmac, hashlib, time
from fastapi import FastAPI, Request, HTTPException

app = FastAPI()
SECRET = "whsec_..."  # from your API & Webhooks settings

@app.post("/webhooks/risksurvey")
async def receive(request: Request):
    ts = request.headers.get("X-RiskSurvey-Timestamp", "")
    sig = request.headers.get("X-RiskSurvey-Signature", "")
    body = await request.body()
    if abs(time.time() - int(ts)) > 300:
        raise HTTPException(401, "Stale webhook")
    expected = hmac.new(SECRET.encode(), f"{ts}\\n".encode() + body, hashlib.sha256).hexdigest()
    if not hmac.compare_digest(f"sha256={expected}", sig):
        raise HTTPException(401, "Bad signature")
    event = await request.json()
    # event = {"id", "event", "created_at", "data": {...}}
    return {"ok": True}`}</CodeBlock>
        <p className="text-xs text-slate-500">Events: <code>survey.completed</code> (risk score + findings count), <code>report.finalized</code> (pull PDF via API), <code>eligibility.assessed</code> (full criterion-by-criterion verdict).</p>
      </DocSection>
    </div>
  );
}

export default function ApiWebhooksTab() {
  return (
    <div className="space-y-6" data-testid="api-webhooks-tab">
      <div className="flex items-center gap-2">
        <KeyRound className="w-5 h-5 text-navy" />
        <div>
          <h2 className="font-heading font-bold text-lg text-slate-800">API & Webhooks</h2>
          <p className="text-sm text-slate-500">Wire survey orders into your policy admin or core systems.</p>
        </div>
      </div>
      <ApiKeysSection />
      <WebhooksSection />
      <ApiDocsSection />
    </div>
  );
}
